/**
 * Registers various hooks for updating or modifying the UI elements in specific components such as
 * product grids, top-page options, and product swatches.
 *
 * The hooks manipulate DOM elements to enhance or customize the display and interaction
 * with product reviews, sorting options, and color swatches.
 *
 * The following hooks are registered:
 * - `serp-product-grid`: Enhances product grid items with additional review count and formatting for review averages.
 * - `serp-top-page`: Adds a 'Sort By' label to the sorting dropdown UI if it doesn't already exist.
 * - `serp-product-swatches`: Adds additional functionality and elements to color swatch UI based on the number of available color variations.
 *
 * @return {void} No return value. The function alters DOM elements directly.
 */

var __fast_options = __fast_options || {};
__fast_options.disable_ac_shadow_root = true;

function hooks() {
  window.addEventListener('fs-custom-events-product-swatch-on-click', (e) => {
    setTimeout(() => {
      let pricingData = window.fsProductPricing[e.detail.parentID];
      if (pricingData) {
        let priceContainer = pricingData.element.querySelector('.price-container.fs-serp-price .price');
        let compareContainer = pricingData.element.querySelector('.compare-container .compare.fs-compare');
        if (priceContainer) {
          priceContainer.innerText = pricingData.price;
        }
        if (compareContainer && pricingData.compareText) {
          compareContainer.innerText = pricingData.compareText;
        }
      }
    }, 5);
  });
  window.SerpOptions.registerHook("serp-product-grid", ({products, element}) => {
    let searchWrapper = element.closest('.searchResultsWrapper');
    if (searchWrapper) {
      let title = searchWrapper.querySelector('.titleWrapper .title');
      if (title && title.innerText.toLowerCase().trim().includes('search results for')) {
        const url = new URL(window.location.href);
        const searchQuery = url.searchParams.get('q');
        if (searchQuery) {
          title.innerText = `Search Results for "${searchQuery}"`;
        }
        title.closest('.titleWrapper').classList.add('show');
        let productGrid = searchWrapper.querySelector('.products-grid');
        if (productGrid) {
          productGrid.style.setProperty("--grid-template-columns", "repeat(4, 1fr)");
        }
      }
    }

    for (const [productId, product] of Object.entries(products)) {
      let productElement = element.querySelector(`[data-product-id="${productId}"]`);
      if (productElement) {
        if (!window.fsProductPricing) {
          window.fsProductPricing = {};
        }
        window.fsProductPricing[`${productId}`] = {price: product.formattedPrice, element: productElement};
        if (product.hasOwnProperty('reviewCount') && product.reviewCount > 0) {
          let reviewAvgEl = productElement.querySelector('.fs-reviews-avg');
          if (reviewAvgEl && !reviewAvgEl.innerText.includes('(')) {
            reviewAvgEl.innerHTML = `<span class="fs-reviews-avg-el">(${reviewAvgEl.innerText})</span>`;
          }
          let reviewCountEl = productElement.querySelector('.fs-reviews-count');
          if (reviewCountEl && !reviewCountEl.innerText.includes('reviews')) {
            reviewCountEl.innerText = reviewCountEl.innerText.replace('(', '').replace(')', '') + ' reviews';
          }
        }
        if (!productElement.querySelector('.price-holder')) {
          let priceContainer = productElement.querySelector('.price-container.fs-serp-price');
          let compareContainer = productElement.querySelector('.compare-container');
          if (priceContainer) {
            let priceHolder = document.createElement("div");
            priceHolder.classList.add('price-holder');
            priceContainer.parentNode.insertBefore(priceHolder, priceContainer);
            priceHolder.appendChild(priceContainer);
            if (compareContainer) {
              priceHolder.appendChild(compareContainer);
            }
          }
        }
        let compareEl = productElement.querySelector('.compare.fs-compare');
        if (compareEl) {
          let comparePrice = product.formattedCompare;
          let maxComparePrice = product.maxFormattedCompare;
          compareEl.innerText = comparePrice;
          if (maxComparePrice && maxComparePrice !== comparePrice) {
            compareEl.innerText += ` - ${maxComparePrice}`;
          }
          window.fsProductPricing[`${productId}`].compareText = compareEl.innerText;
        }

        // Sale Tag
        let fetchUrl = product.productURL + '?view=product_data';

        // Ensure global tracker exists
        window.fetchingSaleTag = window.fetchingSaleTag || {};

        // Only run if not fetched or fetching yet
        if (
          !productElement.classList.contains('sale-tag-fetched') &&
          !window.fetchingSaleTag[productId]
        ) {
          // Mark as fetching immediately to prevent duplicates
          window.fetchingSaleTag[productId] = true;

          fetch(fetchUrl)
            .then(response => response.json())
            .then(data => {
              // Mark as fetched (only once)
              productElement.classList.add('sale-tag-fetched');

              if (data.sale_tag) {
                // Decode and append HTML safely
                let tempDiv = document.createElement('div');
                tempDiv.innerHTML = data.sale_tag;
                let decodedHTML = tempDiv.textContent || tempDiv.innerText;

                if (decodedHTML) {
                  let infoContainer = productElement.querySelector('.info-container');
                  if (infoContainer) {
                    infoContainer.insertAdjacentHTML('beforeend', decodedHTML);
                  }
                }
              }
            })
            .catch(error => {
              console.error('Error fetching product data:', error);
            })
            .finally(() => {
              // Mark as complete (optional cleanup)
              window.fetchingSaleTag[productId] = false;
            });
        }

      }
    }

  });

  window.SerpOptions.registerHook("serp-top-page", ({element}) => {
    setTimeout(() => {

      let wrapper = element.querySelector('.fs-select-wrapper');

      if (wrapper && !wrapper.querySelector('.fs-select-label')) {
        let label = document.createElement("div");
        label.classList.add('fs-select-label');
        label.innerText = 'Sort By:';
        wrapper.appendChild(label);
      }
    }, 250)
  });

  window.SerpOptions.registerHook("serp-filters", ({facets, element}) => {
    element.querySelectorAll('.color-swatch-container:not(.ele-bound)').forEach((ele)=>{
      ele.addEventListener('click', (e)=>{
        if(e.target.classList.contains('color-swatch-container') && !e.target.classList.contains('color-swatch')) {
          if(ele.querySelector('.color-swatch')) {
            ele.querySelector('.color-swatch').click();
          }
        }
      });
      ele.classList.add('ele-bound');
    });
  });

  window.SerpOptions.registerHook("serp-product-swatches", ({colors, swatchClick, showMoreClick, element}) => {

    let listEl = element.querySelector('.color-swatches');

    if (colors.length > 6 && !listEl.querySelector('.color-swatch-extra-colors')) {
      let extraColorsLength = colors.length - 6;
      let extraColorsText = extraColorsLength === 1 ? 'color' : 'colors';
      let text = `+ ${extraColorsLength} ${extraColorsText}`;

      let link = element.closest('.product-card').querySelector('.title-container').getAttribute('href');

      let colorsCountEl = document.createElement("a");
      colorsCountEl.classList.add('color-swatch-extra-colors');
      colorsCountEl.innerText = text;
      colorsCountEl.setAttribute('href', link);
      colorsCountEl.setAttribute('tabindex', '0');
      colorsCountEl.setAttribute('role', 'button');
      colorsCountEl.setAttribute('aria-label', text);
      listEl.appendChild(colorsCountEl);
    }

      setTimeout(function() {
        if (colors.length === 1 && listEl.querySelectorAll('.color-swatch-container').length < 1) {

        let resultClass = '';
        let randomSwatchEl = element.closest('.searchResultsWrapper').querySelector('.color-swatch');

        if (randomSwatchEl) {
          randomSwatchEl.classList.forEach((className) => {
            if (className.indexOf('fs-result-page-') > -1) {
              resultClass = className;
            }
          });
        }

        let color = colors[0];
        let swatchContainer = document.createElement("span");
        swatchContainer.classList.add('color-swatch-container');
        swatchContainer.classList.add('fs-color-swatch-container');
        swatchContainer.setAttribute('tabindex', '0');
        swatchContainer.setAttribute('role', 'button');
        swatchContainer.setAttribute('aria-label', `; Variant: ${color.value}`);

        let colorHandle = color.value.toLowerCase().replace(/\s/g, '-');

        let swatchElement = document.createElement("span");
        swatchElement.classList.add('color-swatch');
        swatchElement.classList.add('fast-swatch-color-fallback');
        swatchElement.classList.add(`fast-swatch-color-${colorHandle}`);
        swatchElement.classList.add(`isp-product-color-swatch-${colorHandle}`);
        if (resultClass) {
          swatchElement.classList.add(resultClass);
        }
        swatchElement.setAttribute('data-disabled', 'false');
        swatchElement.setAttribute('style', '--swatch-width:25px;--swatch-height:25px');
        //swatchElement.setAttribute('title', color.value);
        swatchElement.addEventListener('click', swatchClick);

        swatchContainer.appendChild(swatchElement);
        listEl.appendChild(swatchContainer);
        }
      },1)

  });
}

function autocompleteHooks() {
  window.FastAutocomplete.registerHook('render-autocomplete-product-item', ({product, index, element}) => {
    if (product.hasOwnProperty('reviews_count') && product.reviews_count > 0) {
      if (element.querySelector('.yotpo')) {
        element.querySelector('.yotpo').remove();
      }
      if (element.querySelector('.yotpo-widget-instance')) {
        element.querySelector('.yotpo-widget-instance').remove();
      }
      let reviews = document.createElement('div');
      reviews.classList.add('yotpo');
      reviews.classList.add('bottomLine');
      reviews.dataset.productId = product.id;
      reviews.dataset.url = product.u;

      element.querySelector('.info').insertBefore(reviews, element.querySelector('.fs-product-title'));
      if (typeof yotpoWidgetsContainer != "undefined" && !element.querySelector('.yotpo-widget-instance')) {
        yotpoWidgetsContainer.initWidgets();
      }
      // Add observer for .info element modifications
      let infoElement = element.querySelector('.info');
      if (infoElement) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' || mutation.type === 'subtree') {
              let bottomLineText = infoElement.querySelector('.yotpo-sr-bottom-line-text');
              if (bottomLineText) {
                let text = bottomLineText.innerText.replace(' Reviews', '').trim();
                if (!text.includes('(')) {
                  bottomLineText.innerHTML = '(' + text + ')';
                }
              }
            }
          });
        });
        
        observer.observe(infoElement, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }
    }
  });
}

// execution here
if (window.SerpOptions) {
  hooks();
} else {
  window.addEventListener('fast-serp-ready', function () {
    hooks();
  });
}
if (window.FastAutocomplete) {
  autocompleteHooks();
} else {
  window.addEventListener('fast-autocomplete-ready', function () {
    autocompleteHooks();
  });
}